import { Box, Button, ChakraProvider, Select } from "@chakra-ui/react";
import React, { useCallback, useState } from "react";

import {
  patchSangjoQuotationMemo,
  postSangjoQuotationRequest,
} from "../../apis";

/**
 * 리다이렉트 대상 platform 오리진.
 * GOI_WEB_URL은 프로덕션 하드코딩이라 dev 플랫폼으로 붙어보려면 코드를 고쳐야 했고,
 * 그 편집이 그대로 커밋될 위험이 있었다. env로 덮을 수 있게 한다.
 * 미설정이면 항상 프로덕션(GOI_WEB_URL).
 */
const platformUrl = () =>
  process.env.NEXT_PUBLIC_PLATFORM_URL_OVERRIDE || GOI_WEB_URL;
import {
  URGENCY_OPTIONS,
  CONDOLER_OPTIONS,
  GOI_WEB_URL,
  getSangjoQuotationStatus,
} from "../../constants";
import { chakraTheme, colors } from "../../styles";
import { customEvent } from "../../utils";
import { EventDomainType, EventPathType } from "../../types";
import { VStack } from "../common";

interface RegionColor {
  c100: string;
  c200: string;
  c300: string;
  c400: string;
  c500: string;
}

interface ParlorQuickInquiryProps {
  companyName: string;
  sido: string;
  sigungu: string;
  parlorId: number | string;
  eventDomain: keyof typeof EventDomainType;
  eventPath: EventPathType;
  /** 포털 regionColor (metro=blue, ch=green 등). 버튼·포커스·배지에 사용 */
  mainColor: RegionColor;
}

/**
 * "빠르게 알아보기" 인라인 견적 카드 (T1 실험).
 * 긴급도 + 조문객수 선택 → 익명 게스트 견적 POST → goifuneral 로그인으로 리다이렉트(qid 전달).
 * 실패 시 기존 CTA로 fail-open.
 */
export default function ParlorQuickInquiry({
  companyName,
  sido,
  sigungu,
  parlorId,
  eventDomain,
  eventPath,
  mainColor,
}: ParlorQuickInquiryProps) {
  const [urgency, setUrgency] = useState(URGENCY_OPTIONS[0]);
  const [condolers, setCondolers] = useState("100명 내외");
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(async () => {
    if (loading) return;
    setLoading(true);

    // 성공/409/실패 어느 경로로 나가든 붙는 UTM. 표면은 항상 parlor_card다.
    const utm = `utm_source=dogsound&utm_content=${eventDomain}&utm_medium=parlor_card`;

    customEvent({
      domain: eventDomain,
      path: eventPath,
      trigger_and_target: "qk-send",
    });

    const needs_binso = condolers !== "0명(무빈소)";
    const condolersValue = condolers === "0명(무빈소)" ? "0명" : condolers;

    try {
      const { data } = await postSangjoQuotationRequest({
        status: urgency,
        funeral_sido: sido,
        funeral_sigungu: sigungu,
        needs_binso,
        condolers: condolersValue,
      });
      customEvent({
        domain: eventDomain,
        path: eventPath,
        trigger_and_target: "qk-done",
      });
      // 위 개별 이벤트와 같은 시점의 공통 이벤트. Hackle 지표에 이거 하나만 넣으면
      // 6개 포털이 합산된다(개별 이벤트는 이름에 domain이 박혀 6개를 나열해야 함).
      customEvent({
        action: "qk_created",
        properties: { portal: eventDomain },
      });
      // 출처를 견적 row에 남긴다. platform 로그인 body의 memo는 슬랙 알림에만 쓰이고
      // DB에 저장되지 않으므로 여기서 직접 PATCH해야 어드민에서 출처가 보인다.
      // 실패해도 리드 흐름은 막지 않는다(부기는 운영 편의, 리드가 우선).
      // 문구는 platform parlor-requests 제출 관례("{장례식장명} 개별 견적 페이지에서 제출한 견적")를 따른다.
      // 포털 구분은 memo에 넣지 않는다. utm_content로 GA4에서 읽으면 되고, 상담원에게 필요한 건 장례식장명이다.
      await patchSangjoQuotationMemo(
        data.id,
        `${companyName} 페이지에서 제출한 견적`
      ).catch(() => {});

      // platform이 quotation_request 쿠키를 조립할 수 있도록 견적 값을 함께 넘긴다.
      // 크로스도메인이라 dogsound가 goifuneral.co.kr 쿠키를 직접 심을 수 없고,
      // 백엔드에 GET retrieve가 없어서(viewset이 Create+Update만) qid로 조회도 불가하다.
      // status는 서버 전송값과 동일해야 platform 장례준비긴급도_LIST_EVENT_KEY 매핑에 걸린다.
      // parlor_id: 어느 장례식장 견적인지. platform 로그인 화면이 장례식장명을 되짚는 데 쓴다.
      // 크로스도메인이라 쿠키로 못 넘기고, `/match/parlor-requests/{parlorId}/` 로 이미
      // 보내고 있어 id 체계는 platform 과 같다.
      //
      // parlor.goifuneral.co.kr 핸드오프와 달리 현재 페이지 쿼리를 물려받지 않는다.
      // 그쪽은 광고 파라미터(`gclid`, `n_query`)를 platform 까지 나르려고 승계하는데,
      // dogsound 는 광고를 돌리지 않아 나를 것이 없다. 승계하면 방문자 UTM 과 아래에서
      // 덧붙이는 dogsound 자체 UTM 이 겹쳐 `utm_source` 가 배열이 되고, 그 값으로 재는
      // dogsound 리드 집계만 깨진다. 광고를 시작하면 그때 parlor 쪽 방식을 가져온다.
      const params = new URLSearchParams({
        redirectTo: "/match/request/",
        event_from: "dogsound_card",
        qid: String(data.id),
        status: getSangjoQuotationStatus(urgency),
        sido,
        sigungu,
        parlor_id: String(parlorId),
      });
      // 공백을 + 대신 %20으로 보낸다. URLSearchParams.toString()은 표준대로 공백을 +로
      // 직렬화하고 정상 쿼리 파서는 되돌리지만, 받는 쪽이 decodeURIComponent를 한 번 더 걸면
      // +가 공백으로 안 바뀌어 status가 매핑 키와 안 맞는다(분기 전체 실패).
      // toString()이 리터럴 +는 %2B로 인코딩하므로, 남은 +는 공백뿐이라 치환이 안전하다.
      const query = params.toString().replace(/\+/g, "%20");
      window.location.href = `${platformUrl()}/login/?${query}&${utm}`;
    } catch (e: any) {
      // 409 = 백엔드 create의 "Request already exists." (로그인 상태 + 기존 견적 보유).
      // Axios가 withCredentials라 dogsound 도메인에서도 goifuneral 세션이 API에 실려 가므로
      // 재방문 로그인 유저는 이 경로를 탄다. 진짜 실패(CORS/네트워크)와 섞으면 이벤트가 오염된다.
      // 이미 로그인 상태이니 로그인 페이지를 거치지 않고 견적 요청 페이지로 보낸다.
      // MatchRequestContainer가 exists 조회로 "이미 견적 있음" 모달을 띄운다(platform 기존 UX).
      if (e?.response?.status === 409) {
        customEvent({
          domain: eventDomain,
          path: eventPath,
          trigger_and_target: "qk-dup",
        });
        window.location.href = `${platformUrl()}/match/request/?${utm}`;
        return;
      }

      // fail-open: 견적 생성 실패해도 유저 흐름은 막지 않고 기존 CTA로 넘김
      customEvent({
        domain: eventDomain,
        path: eventPath,
        trigger_and_target: "qk-fail",
      });
      window.location.href = `${platformUrl()}/match/parlor-requests/${parlorId}/?${utm}`;
    }
  }, [
    loading,
    urgency,
    condolers,
    sido,
    sigungu,
    parlorId,
    eventDomain,
    eventPath,
  ]);

  // 두 셀렉트 완전 동일. 평소 회색 테두리, 포커스 때만 포인트 컬러.
  const selectStyle = {
    height: "52px",
    fontSize: "16px",
    color: colors.gray900,
    background: colors.white,
    borderColor: colors.gray300,
    borderRadius: "12px",
    _hover: { borderColor: colors.gray300 },
    _focusVisible: {
      borderColor: mainColor.c400,
      boxShadow: `0 0 0 1px ${mainColor.c400}`,
    },
  } as const;

  const labelStyle = {
    color: colors.gray700,
    fontSize: "13px",
    fontWeight: 600,
  } as const;

  return (
    <ChakraProvider theme={chakraTheme}>
      <Box background={mainColor.c100} padding="28px 20px" my="16px">
        <Box maxWidth="720px" mx="auto">
          <VStack
            width="100%"
            gap={20}
            alignItems="flex-start"
            padding="24px"
            borderRadius="16px"
            style={{
              background: colors.white,
              border: `1px solid ${colors.gray200}`,
              boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
            }}
          >
            {/* 헤더: 배지 + 제목 (중복 소제목 제거) */}
            <VStack width="100%" gap={10} alignItems="flex-start">
              <Box
                padding="4px 10px"
                borderRadius="8px"
                fontSize="12px"
                fontWeight={700}
                style={{ background: mainColor.c100, color: mainColor.c500 }}
              >
                무료 · 30초
              </Box>
              <Box
                width="100%"
                fontSize="19px"
                fontWeight={700}
                style={{ color: colors.gray900, lineHeight: 1.4 }}
              >
                {companyName} 예상비용 빠르게 알아보기
              </Box>
            </VStack>

            {/* 입력 그룹: 라벨-셀렉트 간격 8, 그룹 간격 16 */}
            <VStack width="100%" gap={16} alignItems="flex-start">
              <VStack width="100%" gap={8} alignItems="flex-start">
                <Box {...labelStyle}>긴급도</Box>
                <Select
                  value={urgency}
                  onChange={(e) => setUrgency(e.target.value)}
                  {...selectStyle}
                >
                  {URGENCY_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </Select>
              </VStack>

              <VStack width="100%" gap={8} alignItems="flex-start">
                <Box {...labelStyle}>조문객 수</Box>
                <Select
                  value={condolers}
                  onChange={(e) => setCondolers(e.target.value)}
                  {...selectStyle}
                >
                  {CONDOLER_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </Select>
              </VStack>
            </VStack>

            <Button
              width="100%"
              backgroundColor={mainColor.c400}
              color={colors.white}
              height="54px"
              borderRadius="12px"
              fontSize="17px"
              fontWeight={700}
              isLoading={loading}
              onClick={handleSubmit}
              _hover={{ backgroundColor: mainColor.c500 }}
              _active={{ backgroundColor: mainColor.c500 }}
            >
              내 조건으로 확인하기
            </Button>

            <Box
              width="100%"
              fontSize="13px"
              style={{ color: colors.gray600, lineHeight: 1.5 }}
            >
              신뢰할 수 있는 <b>보건복지부 e하늘 장사 정보시스템</b>을 통해
              <b>고이장례연구소가 자체 추산한 예상 비용</b>이에요.
            </Box>
          </VStack>
        </Box>
      </Box>
    </ChakraProvider>
  );
}
